import { useTranslation } from "react-i18next";
import StudentModel from "../../../api/models/StudentModel";
import UserProfilePictureAvatar from "../../../components/avatar/UserProfilePictureAvatar";
import TableHeaderCell, {
  SortDirection,
} from "../../../components/table-cell/TableHeaderCell";
import { useNavigate } from "react-router-dom";

type StudentsTableProps = {
  students: StudentModel[];
  getSortingDirection: (colName: string) => SortDirection | undefined;
  handleSetSortingOrder: (colName: string) => () => void;
};
export default function StudentsTable(props: StudentsTableProps) {
  const { students, getSortingDirection, handleSetSortingOrder } = props;
  const LocalBasePath = "page.liaison.dashboard.readers";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const navigate = useNavigate();

  return (
    <table className="min-w-full">
      <thead className="bg-gray-50 text-start text-xs font-medium text-gray-500 border-b border-gray-200">
        <tr className="whitespace-nowrap">
          <TableHeaderCell
            sortable={true}
            sortingDirection={getSortingDirection("full_name")}
            onClick={handleSetSortingOrder("full_name")}
          >
            {t("readerName")}
          </TableHeaderCell>
          <TableHeaderCell>{t("tutorName")}</TableHeaderCell>
          <TableHeaderCell
            sortable={true}
            sortingDirection={getSortingDirection("first_session_at")}
            onClick={handleSetSortingOrder("first_session_at")}
          >
            {t("startingDate")}
          </TableHeaderCell>
          <TableHeaderCell
            sortable={true}
            sortingDirection={getSortingDirection("reached_page")}
            onClick={handleSetSortingOrder("reached_page")}
          >
            {t("finishedPages")}
          </TableHeaderCell>
          <TableHeaderCell>{t("weeklySessionsCount")}</TableHeaderCell>
          <TableHeaderCell>{t("scheduledSessions")}</TableHeaderCell>
          <TableHeaderCell>
            <span className="sr-only">info</span>
          </TableHeaderCell>
        </tr>
      </thead>
      <tbody data-cy="tableBody">
        {students?.map((student, index) => {
          return (
            <tr
              key={`StudentInfoTable_${index}_${student.id}`}
              className={" border-b cursor-pointer bg-white hover:bg-gray-50"}
              onClick={() => {
                navigate(`/liaison/dashboard/student/${student.id}`);
              }}
            >
              <td className="whitespace-nowrap py-4 ps-4 pe-3 text-sm sm:ps-6">
                <div className="flex items-center gap-3">
                  <div className=" flex-shrink-0">
                    <UserProfilePictureAvatar
                      profilePictureUrl={student?.profile_picture_url}
                      className="w-10 h-10"
                    />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {student.fullName}
                    </div>
                    <div className="text-gray-500">
                      {student.sponsorship?.sponsor?.name && (
                        <span className="flex justify-start gap-1">
                          <span>{student.sponsorship.sponsor.name}</span>
                          <span>{t("backSlash")}</span>
                          <span>{student.sponsorship.id}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-4" />
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div className="flex items-center gap-3">
                  <div className=" flex-shrink-0">
                    <UserProfilePictureAvatar
                      profilePictureUrl={student?.tutor?.profile_picture_url}
                      className="w-10 h-10"
                    />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {student?.tutor?.full_name}
                    </div>
                    <a
                      href={`mailto:${student?.tutor?.account?.email}`}
                      title={student?.tutor?.account?.email}
                      className="text-gray-500"
                    >
                      {student?.tutor?.account?.email}
                    </a>
                  </div>
                  <div className="px-4" />
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div className="flex justify-start">
                  {student.firstSessionDate?.format("L")}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div className="flex justify-start">
                  {student.lastQuranPageReached}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div className="flex justify-start">
                  {student.weeklySessionsCount}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div className="flex justify-start">
                  {`${student.finished_sessions_count} / ${student.total_sessions_count}`}
                </div>
              </td>

              <td className="relative py-4 ps-3 pe-4 sm:pe-6">
                {/* Uncomment the following after implementing the clicking action of the scheduled students*/}
                {/* <div className="flex justify-end">
                <ChevronLeftIcon
                  className="h-4 w-4 text-gray-400 ltr:rotate-180"
                  aria-hidden="true"
                />
              </div>
              <span className="sr-only">Show info</span> */}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
